<template>
    <div class="container container-with-transition student-container">
        <transition :name="componentTransition">
            <component :is="currentPage" class="student-page"></component>
        </transition>
    </div>
</template>

<script>
    import * as extend from 'extend'

    import SplashPage from './flow/0-splash'
    import ConnectPage from './flow/1-connect'
    import GamePage from './flow/2-game'

    import {ConsoleLogger} from '@/console-logger'
    import {$eBus} from '@/event-bus'

    import * as MutationTypes from '@/store/mutation-types'

    import GameClientEvent from '@/enum/GameClientEvent'
    import GamePhase from '@/enum/GamePhase'
    import GameType from '@/enum/GameType'
    import GameClientType from '@/enum/GameClientType'
    import AppConfig from '@/app-config'
    import {getStudentUrl} from '@/utils/UrlUtil'
    import KickReason from "@/enum/KickReason";
    import {webSocketCodeToString, CLOSE_ABNORMAL} from '@/utils/WebSocketCodeUtil';

    const pageComponents = {
        'splash': SplashPage,
        'connect': ConnectPage,
        'game': GamePage
    }

    const components = {}

    export default {
        components: extend(components, pageComponents),
        data() {
            return {
                leftRoom: true,
                componentTransition: AppConfig.componentTransition,
                currentPage: 'splash',
                logger: new ConsoleLogger('StudentController', 'background: #00aaaa; color: white', null, AppConfig.debug.studentController),
            }
        },
        watch: {
            '$route'(to, from) {
                this.reloadCurrentPage(to)
            }
        },
        methods: {
            resetGame(resetError = true, disconnect = false) {
                if (resetError) {
                    this.$store.dispatch('fullReset');
                } else {
                    this.$store.dispatch('fullResetWithoutError');
                }

                if (disconnect) {
                    this.leftRoom = true;
                    this.$gameClient.disconnect();
                }

                this.$nextTick(() => {
                    this.$router.push(getStudentUrl('connect')); // Go back to connection screen
                });
            },
            onRoomStateChanged(room, state) {
                let roomState = room && room.state ? room.state : {}

                console.log('onRoomStateChanged', room, roomState, state);

                if (roomState.hasOwnProperty('currentPageIndex') && roomState.currentPageIndex >= 0) {
                    this.$store.commit(MutationTypes.SET_CURRENT_PAGE_INDEX, roomState.currentPageIndex)
                }

                if (roomState.hasOwnProperty('pagePhase')) {
                    this.$store.commit(MutationTypes.SET_CURRENT_PAGE_PHASE, roomState.pagePhase)
                }
            },
            onPhaseChanged(newPhase) {
                if (newPhase === GamePhase.SelectTheme || newPhase === GamePhase.SelectCase) {
                    this.$store.dispatch('fullReset');

                    this.$router.push(getStudentUrl('connect'));
                } else if (newPhase === GamePhase.Lobby || newPhase === GamePhase.Pages || newPhase === GamePhase.End) {
                    this.$router.push(getStudentUrl('game'));
                }
            },
            onRoomJoined(room) {
                this.$store.commit(MutationTypes.SET_IS_READY, false);

                console.log('onRoomJoined');
                this.leftRoom = false;
            },
            onRoomLeave(room, code) {
                if (!this.leftRoom) {
                    console.log('We unexpectedly left the room! Perhaps we were kicked?');
                }

                let resetIncludeErrors = true;

                if (code >= 4000 && code <= 4999) {
                    console.log(`We were kicked: KickReason.${code}`);

                    if (code === KickReason.MissingLicenseInfo) {
                        console.log(`We were kicked: KickReason.MissingLicenseInfo`);
                        this.$store.commit(MutationTypes.SET_ERROR_ICON, 'connectionError')
                        this.$store.commit(MutationTypes.SET_ERROR_MESSAGE, 'Det ser ikke ud til, at en lærer har lavet dette rum')
                    } else if (code === KickReason.TooManyPlayersInRoom) {
                        console.log(`We were kicked: KickReason.TooManyPlayersInRoom`);
                        this.$store.commit(MutationTypes.SET_ERROR_ICON, 'connectionError')
                        this.$store.commit(MutationTypes.SET_ERROR_MESSAGE, 'Der ikke er plads til flere elever i dette rum')
                    } else if (code === KickReason.TeacherLeft) {
                        console.log(`We were kicked: KickReason.TeacherLeft`);
                        this.$store.commit(MutationTypes.SET_ERROR_ICON, 'connectionError')
                        this.$store.commit(MutationTypes.SET_ERROR_MESSAGE, 'Rummet er blevet lukket. Du kan nu forbinde til et nyt, hvis du vil')
                    }

                    resetIncludeErrors = false;
                }

                let codeString = webSocketCodeToString(code);

                console.log(`onRoomLeave - code ${code} - code string ${codeString}`);

                if (code === CLOSE_ABNORMAL) {
                    console.log('The close reason was CLOSE_ABNORMAL - this tends to happen when a phone loses connection or pauses the webview - let us try to reconnect instantly');
                    this.$store.commit(MutationTypes.SET_GAME_CLIENT_TRY_RECONNECT, true);

                    this.$gameClient.reset();
                } else {
                    //this.$store.commit(MutationTypes.SET_GAME_CLIENT_TRY_RECONNECT, false);

                    this.$store.dispatch(resetIncludeErrors ? 'fullReset' : 'fullResetWithoutError');
                    this.$gameClient.disconnect();
                }

                console.log('Go back to connection screen!');
                this.$router.push(getStudentUrl('connect')); // Go back to connection screen
            },
            onRoomMessage(room, message) {
                let kickMatch = message.match(/^kick:(\d+)$/);

                if (kickMatch) {
                    let kickReason = parseInt(kickMatch[1], 10);

                    return;
                } else if (message === 'ready') {
                    this.$store.commit(MutationTypes.SET_IS_READY, true);
                }
            },
            reloadCurrentPage(route) {
                let currentPage = route.params.page

                if (!currentPage || !components.hasOwnProperty(currentPage)) {
                    this.logger.logMessage(`[reloadCurrentPage] Unknown StudentPage "${currentPage}"`)

                    this.$router.push(getStudentUrl('splash'))

                    return
                }

                if (this.leftRoom && currentPage === 'game') {
                    this.$router.push(getStudentUrl('connect'))

                    return
                }

                this.logger.logMessage(`[reloadCurrentPage] StudentPage changed to "${currentPage}"`)

                this.currentPage = currentPage
            }
        },
        mounted() {
            this.$store.commit(MutationTypes.SET_GAME_CLIENT_TYPE, GameClientType.Online);
            this.$store.commit(MutationTypes.SET_GAME_TYPE, GameType.ClassRoom);
            this.$store.commit(MutationTypes.SET_GAME_CLIENT_CLOSE_REDIRECT_URL, getStudentUrl('connect'));

            $eBus.$off(GameClientEvent.RoomJoin, this.onRoomJoined)
            $eBus.$off(GameClientEvent.RoomStateChange, this.onRoomStateChanged)
            $eBus.$off(GameClientEvent.RoomLeave, this.onRoomLeave)
            $eBus.$off(GameClientEvent.RoomMessage, this.onRoomMessage)
            $eBus.$off(GameClientEvent.PhaseChanged, this.onPhaseChanged)

            $eBus.$on(GameClientEvent.RoomJoin, this.onRoomJoined)
            $eBus.$on(GameClientEvent.RoomStateChange, this.onRoomStateChanged)
            $eBus.$on(GameClientEvent.RoomLeave, this.onRoomLeave)
            $eBus.$on(GameClientEvent.RoomMessage, this.onRoomMessage)
            $eBus.$on(GameClientEvent.PhaseChanged, this.onPhaseChanged)

            this.reloadCurrentPage(this.$router.currentRoute);
        },
        beforeDestroy() {
            $eBus.$off(GameClientEvent.RoomJoin, this.onRoomJoined)
            $eBus.$off(GameClientEvent.RoomStateChange, this.onRoomStateChanged)
            $eBus.$off(GameClientEvent.RoomLeave, this.onRoomLeave)
            $eBus.$off(GameClientEvent.RoomMessage, this.onRoomMessage)
            $eBus.$off(GameClientEvent.PhaseChanged, this.onPhaseChanged)
        }
    }
</script>
<style lang="less" scoped>
    .student-container {
        position: relative;
        height: 100%;
        width: 100%;
    }

    .student-page {
        text-align: center;
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
    }

    .ready-hand {
        position: absolute;
        top: 50%;
        left: 50%;

        //width: 205px;
        //height: 194px;

        transform: translate(-50%, -50%);
    }
</style>
